import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PlusIcon, GripVerticalIcon, XIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { FormField } from './FormField';
export interface CaptureField {
  id: string;
  type: 'preset' | 'custom';
  preset?: 'name' | 'email' | 'phone' | 'address' | 'company';
  label?: string;
  description?: string;
  required: boolean;
}
interface CaptureFieldsEditorProps {
  fields: CaptureField[];
  onChange: (fields: CaptureField[]) => void;
}
export function CaptureFieldsEditor({
  fields,
  onChange
}: CaptureFieldsEditorProps) {
  const {
    t
  } = useTranslation('common');
  const [newFieldType, setNewFieldType] = useState<'preset' | 'custom'>('preset');
  const [newFieldPreset, setNewFieldPreset] = useState<'name' | 'email' | 'phone' | 'address' | 'company'>('name');
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [newFieldDescription, setNewFieldDescription] = useState('');
  const [newFieldRequired, setNewFieldRequired] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const handleAddField = () => {
    if (newFieldType === 'preset') {
      // Check if this preset already exists
      const presetExists = fields.some(field => field.type === 'preset' && field.preset === newFieldPreset);
      if (presetExists) {
        alert(`A field for "${getPresetLabel(newFieldPreset)}" already exists.`);
        return;
      }
      const newField: CaptureField = {
        id: `preset-${newFieldPreset}-${Date.now()}`,
        type: 'preset',
        preset: newFieldPreset,
        required: newFieldRequired
      };
      onChange([...fields, newField]);
    } else if (newFieldType === 'custom') {
      if (!newFieldLabel.trim()) {
        alert('Please enter a label for the custom field.');
        return;
      }
      const newField: CaptureField = {
        id: `custom-${Date.now()}`,
        type: 'custom',
        label: newFieldLabel,
        description: newFieldDescription,
        required: newFieldRequired
      };
      onChange([...fields, newField]);
      setNewFieldLabel('');
      setNewFieldDescription('');
    }
    setShowAddForm(false);
  };
  const handleRemoveField = (fieldId: string) => {
    onChange(fields.filter(field => field.id !== fieldId));
  };
  const handleMoveField = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0 || direction === 'down' && index === fields.length - 1) {
      return;
    }
    const newFields = [...fields];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    const temp = newFields[index];
    newFields[index] = newFields[targetIndex];
    newFields[targetIndex] = temp;
    onChange(newFields);
  };
  const handleToggleRequired = (fieldId: string) => {
    onChange(fields.map(field => field.id === fieldId ? {
      ...field,
      required: !field.required
    } : field));
  };
  const getPresetLabel = (preset: 'name' | 'email' | 'phone' | 'address' | 'company'): string => {
    const labels = {
      name: 'Full Name',
      email: 'Email Address',
      phone: 'Phone Number',
      address: 'Billing Address',
      company: 'Company Name'
    };
    return labels[preset];
  };
  return <div className="space-y-4" data-id="element-15157" data-mp="3issrcac33qb2">
      <div className="flex justify-between items-center mb-2" data-id="element-15158" data-mp="3irr3t6dvtbcg">
        <h3 className="text-sm font-medium text-gray-700" data-id="element-15159" data-mp="3nrvkawesi62o">
          {t('ui.CaptureFieldsEditor.customer_information_fields')}
        </h3>
        <button type="button" onClick={() => setShowAddForm(!showAddForm)} className="text-sm text-blue-600 hover:text-blue-800 flex items-center" data-id="element-15160" data-mp="3rujxfgajdz5g">
          {showAddForm ? 'Cancel' : <>
              <PlusIcon size={14} className="mr-1" data-id="element-15161" data-mp="22eastw33tacq" />
              {t('ui.CaptureFieldsEditor.add_field')}
            </>}
        </button>
      </div>
      {/* Add new field form */}
      {showAddForm && <div className="bg-gray-50 border border-gray-200 rounded-md p-4 mb-4" data-id="element-15162" data-mp="1262leodd8iqe">
          <div className="mb-3" data-id="element-15163" data-mp="1h2a93ynzg2af">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-15164" data-mp="1h18vbmfl11p0">
              {t('ui.CaptureFieldsEditor.field_type')}
            </label>
            <div className="flex space-x-3" data-id="element-15165" data-mp="odw8db5ierv6">
              <label className="flex items-center" data-id="element-15166" data-mp="oeo4qo6xktmi">
                <input type="radio" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300" checked={newFieldType === 'preset'} onChange={() => setNewFieldType('preset')} data-id="element-15167" data-mp="jet967c7nfv4" />

                <span className="ml-2 text-sm text-gray-700" data-id="element-15168" data-mp="tawetn69twmt">
                  {t('ui.CaptureFieldsEditor.preset_field')}
                </span>
              </label>
              <label className="flex items-center" data-id="element-15169" data-mp="nimy3hfjr9k">
                <input type="radio" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300" checked={newFieldType === 'custom'} onChange={() => setNewFieldType('custom')} data-id="element-15170" data-mp="2cbnjrsm7fonb" />

                <span className="ml-2 text-sm text-gray-700" data-id="element-15171" data-mp="22beja2l6jh24">
                  {t('ui.CaptureFieldsEditor.custom_field')}
                </span>
              </label>
            </div>
          </div>
          {newFieldType === 'preset' ? <div className="mb-3" data-id="element-15172" data-mp="301rkns80daam">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-15173" data-mp="300pz2ttd95en">
                {t('ui.CaptureFieldsEditor.preset_field')}
              </label>
              <select className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" value={newFieldPreset} onChange={e => setNewFieldPreset(e.target.value as any)} data-id="element-15174" data-mp="2q2itf74j29ry">
                <option value="name" data-id="element-15175" data-mp="3hakcwr4892uj">
                  {t('ui.CaptureFieldsEditor.full_name')}
                </option>
                <option value="email" data-id="element-15176" data-mp="3caxsnxpzemc8">
                  {t('ui.CaptureFieldsEditor.email_address')}
                </option>
                <option value="phone" data-id="element-15177" data-mp="37jfzhvu79q1t">
                  {t('ui.CaptureFieldsEditor.phone_number')}
                </option>
                <option value="address" data-id="element-15178" data-mp="32jla5wq2p8iv">
                  {t('ui.CaptureFieldsEditor.billing_address')}
                </option>
                <option value="company" data-id="element-15179" data-mp="2tl9az6b552et">
                  {t('ui.CaptureFieldsEditor.company_name')}
                </option>
              </select>
            </div> : <>
              <div className="mb-3" data-id="element-15180" data-mp="2jn1uspooeodv">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-15181" data-mp="2jo3jhwym79e2">
                  {t('ui.CaptureFieldsEditor.field_label')}
                </label>
                <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder={t('ui.CaptureFieldsEditor.attrs.eg_project_name')} value={newFieldLabel} onChange={e => setNewFieldLabel(e.target.value)} data-id="element-15182" data-mp="hvou6ctqr2nm" />
              </div>
              <div className="mb-3" data-id="element-15183" data-mp="143cb2ualzdzk">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-15184" data-mp="7dlbigngn0mw">
                  {t('ui.CaptureFieldsEditor.field_description_optional')}
                </label>
                <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder={t('ui.CaptureFieldsEditor.attrs.eg_please_provide_the_project_name')} value={newFieldDescription} onChange={e => setNewFieldDescription(e.target.value)} data-id="element-15185" data-mp="bh9jh0g07y8o" />
              </div>
            </>}
          <div className="mb-3" data-id="element-15186" data-mp="2hu7lw5efq8pv">
            <label className="flex items-center" data-id="element-15187" data-mp="2dvrttn3hya5l">
              <input type="checkbox" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" checked={newFieldRequired} onChange={e => setNewFieldRequired(e.target.checked)} data-id="element-15188" data-mp="2dsvw9zvk9nho" />

              <span className="ml-2 text-sm text-gray-700" data-id="element-15189" data-mp="31izzi64u7l85">
                {t('ui.CaptureFieldsEditor.required_field')}
              </span>
            </label>
          </div>
          <div className="flex justify-end" data-id="element-15190" data-mp="3ak6hkjn4x43i">
            <button type="button" onClick={handleAddField} className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm" data-id="element-15191" data-mp="3aj53056wtzh1">
              {t('ui.CaptureFieldsEditor.add_field')}
            </button>
          </div>
        </div>}
      {/* List of fields */}
      {fields.length > 0 ? <div className="border border-gray-200 rounded-md divide-y divide-gray-200" data-id="element-15192" data-mp="3mws2blbb7d3d">
          {fields.map((field, index) => <div key={field.id} className="p-3 flex items-center" data-id="element-15193" data-mp="3rwo55h89syhc" data-mpi={index}>
              <div className="flex-shrink-0 mr-2 text-gray-400" data-id="element-15194" data-mp="3e2dqxttwgmpj">
                <GripVerticalIcon size={16} data-id="element-15195" data-mp="3i48oqocmghyk" />
              </div>
              <div className="flex-1" data-id="element-15196" data-mp="3i2nxp8pinh5g">
                <div className="flex justify-between items-center" data-id="element-15197" data-mp="344pumzgnb759">
                  <div data-id="element-15198" data-mp="3942gbgvs6869">
                    <span className="font-medium text-gray-800" data-id="element-15199" data-mp="394lpa83gdm85">
                      {field.type === 'preset' ? getPresetLabel(field.preset!) : field.label}
                    </span>
                    {field.required && <span className="ml-1 text-red-500 text-xs" data-id="element-15200" data-mp="tcy2h5i5la32">*</span>}
                  </div>
                  <div className="flex items-center space-x-2" data-id="element-15201" data-mp="xf1hu57y0ytm">
                    <button type="button" onClick={() => handleToggleRequired(field.id)} className={`text-xs px-2 py-1 rounded ${field.required ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'}`} data-id="element-15202" data-mp="xcf75wpra376">
                      {field.required ? 'Required' : 'Optional'}
                    </button>
                    <button type="button" onClick={() => handleMoveField(index, 'up')} disabled={index === 0} className={`text-gray-400 hover:text-gray-600 ${index === 0 ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-15203" data-mp="2dd2phffuwlfb">
                      <ChevronUpIcon size={16} data-id="element-15204" data-mp="2w3aktbdbe7ky" />
                    </button>
                    <button type="button" onClick={() => handleMoveField(index, 'down')} disabled={index === fields.length - 1} className={`text-gray-400 hover:text-gray-600 ${index === fields.length - 1 ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-15205" data-mp="2w5dk6m0cxd2c">
                      <ChevronDownIcon size={16} data-id="element-15206" data-mp="186b7qchlpmms" />
                    </button>
                    <button type="button" onClick={() => handleRemoveField(field.id)} aria-label="Remove field" className="text-gray-400 hover:text-red-500" data-id="element-15207" data-mp="1n2irjbp3km1j">
                      <XIcon size={16} aria-hidden="true" data-id="element-15208" data-mp="uc0k5yhcbyib" />
                    </button>
                  </div>
                </div>
                {field.type === 'custom' && field.description && <p className="text-xs text-gray-500 mt-1" data-id="element-15209" data-mp="145r8n3dgw8y0">
                    {field.description}
                  </p>}
                <p className="text-xs text-gray-500 mt-1" data-id="element-15210" data-mp="bmu12acmx4vz">
                  {field.type === 'preset' ? 'Standard input field' : 'Custom input field'}
                </p>
              </div>
            </div>)}
        </div> : <div className="text-center py-6 bg-gray-50 rounded-md border border-gray-200" data-id="element-15211" data-mp="10e1e5bn7ym0k">
          <p className="text-gray-500" data-id="element-15212" data-mp="154zta7wh1e28">
            {t('ui.CaptureFieldsEditor.no_capture_fields_added')}
          </p>
          <p className="text-sm text-gray-400 mt-1" data-id="element-15213" data-mp="1a4m7oko1d0cm">
            {t('ui.CaptureFieldsEditor.add_fields_to_collect_information_from')}
          </p>
        </div>}
      <div className="text-xs text-gray-500 mt-2" data-id="element-15214" data-mp="dg6jarwnwx13">
        {t('ui.CaptureFieldsEditor.these_fields_will_be_shown_on')}
      </div>
    </div>;
}